import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { LightBulbIcon, XMarkIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../common/Button";
import Dropdown from "../Settings/Dropdown";
import { api, BODY_MAX, CATEGORY_LABELS, CATEGORY_ORDER, FeatureRequestCategory, FeatureRequestItem, isTermsRequired, TITLE_MAX } from "./api";
import { useFeatureRequests } from "./FeatureRequestsRouter";
import StatusChip from "./StatusChip";
import VoteButton from "./VoteButton";
import { useVoteToggle } from "./useVoteToggle";

type FormValues = { title: string; body: string; category: FeatureRequestCategory };

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: (created: FeatureRequestItem) => void;
  // Edit mode
  request?: Pick<FeatureRequestItem, "id" | "title" | "body" | "category"> | null;
  onSaved?: (updated: FeatureRequestItem) => void;
}

const field = "w-full rounded-lg border border-gray-300 dark:border-base-300 bg-base-100 px-3 py-2 text-sm text-base-content placeholder:text-base-content/40 outline-none focus:border-blue-500 transition-colors";
const label = "block text-[11px] font-semibold uppercase tracking-wider text-base-content/60 mb-1.5";

// Same shell as NewInvestigationModal. While the title is typed, similar
// existing requests surface underneath so a duplicate gets an upvote
// instead of a second post (the Canny dedupe nudge).
const NewRequestModal: React.FC<Props> = ({ open, onClose, onCreated, request, onSaved }) => {
  const navigate = useNavigate();
  const { requestTerms } = useFeatureRequests();
  const editing = Boolean(request);
  const [form, setForm] = useState<FormValues>({ title: "", body: "", category: "other" });
  const [saving, setSaving] = useState(false);
  const [similar, setSimilar] = useState<FeatureRequestItem[]>([]);
  const titleRef = useRef<HTMLInputElement>(null);
  const { toggle, votingId } = useVoteToggle((id, patch) => setSimilar((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i))));

  useEffect(() => {
    if (!open) return;
    setForm({ title: request?.title || "", body: request?.body || "", category: request?.category || "other" });
    setSimilar([]);
    setTimeout(() => titleRef.current?.focus(), 50);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !saving && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debounced similar-request search on the title (create mode only).
  useEffect(() => {
    if (!open || editing) return;
    const q = form.title.trim();
    if (q.length < 4) {
      setSimilar([]);
      return;
    }
    const t = setTimeout(() => {
      api
        .list({ q, sort: "top" })
        .then((r) => setSimilar(r.items.slice(0, 4)))
        .catch(() => setSimilar([]));
    }, 350);
    return () => clearTimeout(t);
  }, [form.title, open, editing]);

  if (!open) return null;

  const valid = form.title.trim().length >= 5 && form.body.trim().length >= 10;

  const submit = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      if (editing && request) {
        const updated = await api.update(request.id, { title: form.title.trim(), body: form.body.trim(), category: form.category });
        onSaved?.(updated);
      } else {
        const created = await api.create({ title: form.title.trim(), body: form.body.trim(), category: form.category });
        toast.success("Request posted");
        onCreated?.(created);
      }
      onClose();
    } catch (error: any) {
      if (isTermsRequired(error)) requestTerms();
      else toast.error(error?.response?.data?.error || (editing ? "Failed to save" : "Failed to post request"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <dialog open className="modal modal-open">
      <form
        method="dialog"
        className="modal-box max-w-xl p-0 overflow-hidden rounded-2xl"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="flex items-start gap-3 px-6 pt-6 pb-4 border-b border-base-300">
          <div className="size-10 shrink-0 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 flex items-center justify-center">
            <LightBulbIcon className="size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-base-content leading-tight">{editing ? "Edit request" : "New feature request"}</h3>
            <p className="text-sm text-base-content/60 mt-0.5">{editing ? "Update the title or description." : "One idea per request. Describe the problem you're trying to solve, not just the button you want."}</p>
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="p-1 rounded-md text-base-content/50 hover:bg-base-200 hover:text-base-content" title="Close">
            <XMarkIcon className="size-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div>
            <div className="flex items-baseline justify-between">
              <label className={label} htmlFor="fr-title">Title</label>
              <span className="text-[11px] tabular-nums text-base-content/40">{form.title.length}/{TITLE_MAX}</span>
            </div>
            <input id="fr-title" ref={titleRef} className={field} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Let me reorder the sections of a report" maxLength={TITLE_MAX} />
            {similar.length > 0 && (
              <div className="mt-2 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-900/20 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-300 mb-2">Already requested? Upvote instead</p>
                <ul className="space-y-1.5">
                  {similar.map((s) => (
                    <li key={s.id} className="flex items-center gap-2">
                      <VoteButton size="sm" count={s.vote_count} voted={s.hasVoted} busy={votingId === s.id} onToggle={() => toggle(s)} />
                      <button
                        type="button"
                        className="min-w-0 flex-1 text-left text-sm text-base-content/80 hover:text-blue-600 dark:hover:text-blue-300 truncate"
                        onClick={() => {
                          onClose();
                          navigate(`/feature-requests/${s.id}`);
                        }}
                      >
                        {s.title}
                      </button>
                      <StatusChip status={s.status} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div>
            <label className={label}>Area</label>
            <Dropdown size="sm" menuAlign="start" options={CATEGORY_ORDER.map((c) => ({ value: c, label: CATEGORY_LABELS[c] }))} value={form.category} onChange={(v) => setForm({ ...form, category: v as FeatureRequestCategory })} />
          </div>

          <div>
            <div className="flex items-baseline justify-between">
              <label className={label} htmlFor="fr-body">Details</label>
              <span className="text-[11px] tabular-nums text-base-content/40">{form.body.length}/{BODY_MAX}</span>
            </div>
            <textarea id="fr-body" rows={5} className={cn(field, "resize-none leading-5")} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="What are you trying to do, where does it get in the way today, and what would ideal look like?" maxLength={BODY_MAX} />
            <p className="mt-1.5 text-[11px] text-base-content/50">Visible to officers at every agency. No case numbers, names, or other case details.</p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 bg-base-200/60 border-t border-base-300">
          <span className="text-[11px] text-base-content/40 hidden sm:inline">Esc to cancel</span>
          <div className="flex items-center gap-2 ml-auto">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="sm" disabled={!valid || saving}>
              {saving ? "Saving…" : editing ? "Save" : "Post request"}
            </Button>
          </div>
        </div>
      </form>
      <div className="modal-backdrop bg-black/40" onClick={() => !saving && onClose()} />
    </dialog>
  );
};

export default NewRequestModal;
